import React, { useEffect, useState, PropsWithChildren, useContext, cloneElement } from 'react';
import { Tooltip, TooltipPlacement } from '@heroui/tooltip';
import { WalkthruItem } from '../shared/walkthru';
import { Button } from '@heroui/button';
import { WalkthruContext } from '../app/contexts';
import { CaretRightIcon, XIcon, CaretLeftIcon } from '@phosphor-icons/react';
interface WalkthruProps {
  walkthruName: string;
  index: number;
  placement?: TooltipPlacement;
  width?: string; // max-width of the tooltip
  height?: string; // max-height of the tooltip
  children: React.ReactElement;
}
const ONE_HOUR_MS = 60 * 60 * 1000; // ~1 hour
const ONE_DAY_MS = 24 * 60 * 60 * 1000; // ~1 day
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000; // ~1 week
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000; // ~1 month
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000; // ~1 year

const getTimeInMilliseconds = (time: string) => {
  if (time.indexOf(':') === -1) {
    // hours only
    return parseInt(time, 10) * 60 * 60 * 1000;
  }
  const [years, months, days, hours, minutes, seconds] = time.split(':').map(Number);
  return (years * 365 * 24 * 60 * 60 + months * 30 * 24 * 60 * 60 + days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds) * 1000;
};
const Walkthru: React.FC<WalkthruProps> = ({
  children,
  walkthruName,
  index,
  placement,
  width,
  height
}: PropsWithChildren<WalkthruProps>) => {
  const [showWalkthru, setShowWalkthru] = useState<boolean>(false);
  const [walkthruItems, setWalkthruItems] = useState<WalkthruItem[]>([]);
  const [hideForSession, setHideForSession] = useState<boolean>(false);
  const [sessionStorageUpdateTime, setSessionStorageUpdateTime] = useState<number>(0);
  const {
    walkthrus,
    setWalkthru,
    availableWalkthrus
  } = useContext(WalkthruContext);
  useEffect(() => {
    const sessionWalkthrus = sessionStorage.getItem("walkthrus");
    if (!!sessionWalkthrus) {
      const sessionWalkthrusObject = JSON.parse(sessionWalkthrus);
      if (sessionWalkthrusObject[walkthruName]?.currentIndex === index && sessionWalkthrusObject[walkthruName]?.hideForSession) {
        setHideForSession(true);
      }
    }
    const reminderTime = walkthrus?.[walkthruName]?.reminder || 0;
    let waitingForReminder = reminderTime > 0 ? true : false;
    if (reminderTime > 0 && reminderTime < Date.now()) {
      waitingForReminder = false;
    }
    setShowWalkthru(walkthrus?.[walkthruName]?.currentIndex === index && !waitingForReminder);
  }, [walkthrus?.[walkthruName]?.currentIndex, index, sessionStorageUpdateTime]);
  useEffect(() => {
    if (!availableWalkthrus || !availableWalkthrus?.[walkthruName]) return;
    if (availableWalkthrus?.[walkthruName]) {
      setWalkthruItems(availableWalkthrus?.[walkthruName] || []);
    }
    const sessionWalkthrus = sessionStorage.getItem("walkthrus");
    if (!!sessionWalkthrus) {
      const sessionWalkthrusObject = JSON.parse(sessionWalkthrus);
      if (sessionWalkthrusObject[walkthruName]?.currentIndex === index && sessionWalkthrusObject[walkthruName]?.hideForSession) {
        setHideForSession(true);
      }
    }
    if (!walkthrus || !availableWalkthrus) return;
    if (walkthrus[walkthruName]) {
      const reminderTime = walkthrus?.[walkthruName]?.reminder || 0;
      let waitingForReminder = reminderTime > 0 ? true : false;
      if (reminderTime > 0 && reminderTime < Date.now()) {
        waitingForReminder = false;
      }
      setShowWalkthru(walkthrus[walkthruName].currentIndex === index && !waitingForReminder);
    } else {
      // Only set walkthru state if cookies are enabled for this walkthru
      setWalkthru(walkthruName, {
        walkthruName,
        currentIndex: 0,
        reminder: 0
      });
    }
  }, [walkthrus?.[walkthruName], index, availableWalkthrus, sessionStorageUpdateTime]);
  const handleNext = () => {
    if (!walkthrus?.[walkthruName]) {
      return;
    }
    // Only update walkthru state if cookies are enabled for this walkthru
    const updatedWalkthru = {
      ...walkthrus[walkthruName],
      currentIndex: walkthrus[walkthruName].currentIndex + 1,
      reminder: 0
    };
    setWalkthru(walkthruName, updatedWalkthru);
  };
  const handleDismiss = () => {
    if (!walkthrus?.[walkthruName]) return;
    // Only update walkthru state if cookies are enabled for this walkthru
    const reminder = walkthrus[walkthruName].reminder || ONE_DAY_MS;
    const updatedWalkthru = {
      ...walkthrus[walkthruName],
      currentIndex: walkthrus[walkthruName].currentIndex + 1,
      reminder: Date.now() + reminder
    };
    setWalkthru(walkthruName, updatedWalkthru);
  };
  const handleReminder = () => {
    if (!walkthruName) return;
    // Only update walkthru state if cookies are enabled for this walkthru
    const currentItem = walkthruItems[index];
    if (!walkthrus?.[walkthruName]) return;
    const reminderTime = !!currentItem?.reminder ? getTimeInMilliseconds(currentItem?.reminder.toString()) : ONE_HOUR_MS * 2;
    const updatedWalkthru = {
      ...walkthrus[walkthruName],
      currentIndex: walkthrus[walkthruName].currentIndex,
      reminder: Date.now() + reminderTime
    };
    setWalkthru(walkthruName, updatedWalkthru);
  };
  const handleDontShowAgain = () => {
    if (!walkthruName) return;
    // Only update walkthru state if cookies are enabled for this walkthru
    const currentItem = walkthruItems[index];
    if (!walkthrus?.[walkthruName]) return;
    const dontShowAgainReminder = !!currentItem?.dontShowAgainReminder ? getTimeInMilliseconds(currentItem?.dontShowAgainReminder) : ONE_MONTH_MS * 3;
    const updatedWalkthru = {
      ...walkthrus[walkthruName],
      currentIndex: walkthrus[walkthruName].currentIndex,
      reminder: Date.now() + dontShowAgainReminder
    };
    setWalkthru(walkthruName, updatedWalkthru);

    // Hide the current walkthru
    setShowWalkthru(false);
  };
  const handlePrevious = () => {
    if (!walkthrus?.[walkthruName] || walkthrus[walkthruName].currentIndex <= 0) {
      return;
    }
    // Only update walkthru state if cookies are enabled for this walkthru
    const updatedWalkthru = {
      ...walkthrus[walkthruName],
      currentIndex: walkthrus[walkthruName].currentIndex - 1,
      reminder: 0
    };
    setWalkthru(walkthruName, updatedWalkthru);
  };
  const handleHideForSession = () => {
    if (!walkthrus?.[walkthruName]) return;
    const updatedWalkthru = {
      ...walkthrus[walkthruName],
      hideForSession: true
    };
    let sessionWalkthrusObject = {};
    const sessionWalkthrus = sessionStorage.getItem("walkthrus");
    if (!!sessionWalkthrus) {
      sessionWalkthrusObject = JSON.parse(sessionWalkthrus);
    }
    const newSessionWalkthrusObject = {
      ...sessionWalkthrusObject,
      [walkthruName]: updatedWalkthru
    };
    sessionStorage.setItem('walkthrus', JSON.stringify(newSessionWalkthrusObject));
    setSessionStorageUpdateTime(Date.now());
  };
  const handleTargetClick = (e: React.MouseEvent) => {
    if (showWalkthru) {
      // For walkthrus without cookies, just hide the tooltip
      const currentItem = walkthruItems[index];
      if (currentItem.noAutoIncrement) {
        handleHideForSession();
      } else {
        handleNext();
      }
    }
  };
  const enhancedChildren = cloneElement(children, {
    onClick: (e: React.MouseEvent) => {
      handleTargetClick(e);
      children.props.onClick?.(e);
    }
  });
  if (!availableWalkthrus || !availableWalkthrus?.[walkthruName]) {
    return enhancedChildren;
  }
  return <Tooltip id={`${walkthruName}-${index}`} className='bg-white p-0 z-[100001] sm:flex hidden' classNames={{
    base: 'sm:flex hidden before:bg-white before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px] transition-all duration-300 ease-in-out',
    content: 'text-wrap p-0 rounded-md border-[8px] border-solid border-white drop-shadow-none shadow-none transition-all duration-300 ease-in-out'
  }} content={<div className="flex flex-col animate-fadeIn" style={{
    maxWidth: width,
    maxHeight: height
  }}>
          {walkthruItems[index]?.title && <div className="box-border flex flex-row justify-between items-center bg-brand-blue-dark p-2 rounded-md text-sm sm:text-base m-0 leading-none border-2 border-t-0 border-l-0 border-solid border-black/10 transition-all duration-300 ease-in-out">
              <div className="flex flex-row items-center gap-2">
                {walkthruItems.length > 1 && <div className="text-sm font-light bg-white/50 text-white/80 rounded-md px-1 py-0.5 border-0 border-solid border-blue-400 tracking-widest shadow-inner shadow-black/5 transition-all duration-300 ease-in-out">
                    {index + 1}/{walkthruItems.length}
                  </div>}
                <div className="text-white text-sm sm:text-lg transition-all duration-300 ease-in-out">
                  {walkthruItems[index]?.title}
                </div>
              </div>
              {walkthruItems[index]?.dismissButton && <Button className="flex justify-center items-center bg-transparent text-white rounded-md p-0 m-0 cursor-pointer min-h-[0px] h-[20px] max-h-[20px] min-w-[0px] w-[20px] max-w-[20px] gap-0 hover:opacity-100 transition-all duration-300 ease-in-out" size="md" onPress={handleDismiss}>
                  <XIcon size={20} className="hover:text-black transition-all duration-300 ease-in-out" />
                </Button>}
            </div>}
          <div className="flex flex-col justify-between m-0">
            <div className="text-white text-sm sm:text-lg font-normal leading-tight p-6 my-2 bg-blue-400 rounded-md border-2 border-t-0 border-l-0 border-solid border-black/40 transition-all duration-300 ease-in-out animate-slideIn">
              {walkthruItems[index]?.description}
            </div>
            <div className="flex flex-row">
              <div className="flex flex-row justify-start w-1/2 gap-2">
                {walkthruItems[index]?.reminder && <Button className="text-blue-300 h-[30px] rounded-md m-0 border-blue-200 border-1 border-solid cursor-pointer hover:bg-blue-100 hover:text-blue-600 transition-all duration-300 ease-in-out" variant="bordered" size="md" onPress={handleReminder}>
                    {walkthruItems[index]?.reminderButton || 'Remind me later'}
                  </Button>}
                {walkthruItems[index]?.showDontShowAgainButton && <Button className="text-gray-600 h-[30px] rounded-md m-0 border-gray-600 border-1 border-solid cursor-pointer hover:bg-gray-400 hover:text-white transition-all duration-300 ease-in-out" variant="bordered" size="md" onPress={handleDontShowAgain}>
                    Don&apos;t show again
                  </Button>}
              </div>
              <div className="flex flex-row justify-end w-1/2 gap-2">  
                {index > 0 && <Button className="bg-blue-100 text-blue-400 h-[30px] rounded-md cursor-pointer m-0 p-0 border-2 border-t-0 border-l-0 border-solid border-black/10 hover:bg-blue-300 hover:text-black transition-all duration-300 ease-in-out animate-slideIn" variant="solid" size="md" onPress={handlePrevious}>
                    <CaretLeftIcon weight="bold" className="w-4 h-4 ml-[-4px] transition-all duration-300 ease-in-out" />
                    Prev
                  </Button>}
                {walkthruItems[index]?.showNextButton !== false && <Button className="bg-blue-100 text-blue-400 h-[30px] rounded-md cursor-pointer m-0 p-0 border-2 border-t-0 border-l-0 border-solid border-black/10 hover:bg-blue-300 hover:text-black transition-all duration-300 ease-in-out animate-slideIn" variant="solid" size="md" onPress={handleNext}>
                    {walkthruItems[index]?.nextButton || 'Next'}
                    <CaretRightIcon weight="bold" className="w-4 h-4 mr-[-4px] transition-all duration-300 ease-in-out" />
                  </Button>}
              </div>
            </div>
          </div>
        </div>} delay={1000} showArrow={true} offset={10} portalContainer={document.body} isOpen={showWalkthru && !hideForSession} placement={placement || 'top'} data-sentry-element="Tooltip" data-sentry-component="Walkthru" data-sentry-source-file="walkThru.tsx">
      {enhancedChildren}
    </Tooltip>;
};
const styles = `
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

.animate-slideIn {
  animation: slideIn 0.3s ease-in-out;
}
`;
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
export default Walkthru;